/* eslint-disable */
import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Clock, CreditCard, Gift, FileText, Mail, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Maker-only "Subscription Requests" queue.
 *
 * Three-state filter: pending · approved · rejected · all.
 * Approve modal supports three modes:
 *   • stripe — generate a Checkout link, email the user.
 *   • trial  — instant N-day no-pay access, welcome email fires.
 *   • manual — instant access + bank-transfer instructions.
 *
 * Reject modal collects a reason and emails the requester respectfully.
 */
export default function SubscriptionsAdmin() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget]   = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/subscriptions/requests?status=${statusFilter}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load requests.');
    } finally { setBusy(false); }
  }, [statusFilter]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div data-testid="subscriptions-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="ve-eyebrow">Subscriptions</div>
          <h1 className="display text-3xl md:text-4xl mt-2">Access approval queue.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Every prospective user lands here. Approve with payment, trial, or
            manual bank transfer — or reject with a respectful note. All
            decisions are audit-logged.
          </p>
        </div>
        <button
          onClick={load}
          disabled={busy}
          data-testid="sa-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="mt-6 inline-flex gap-1 p-1 rounded-md border border-slate-200 bg-slate-50">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`sa-filter-${s}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded transition-colors ${
              statusFilter === s
                ? 'bg-[#8B3FC5] text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {s} {statusFilter === s && `(${items.length})`}
          </button>
        ))}
      </div>

      <div className="mt-6 ve-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-xs">
            <tr>
              <th className="text-left px-5 py-3">Requester</th>
              <th className="text-left px-5 py-3">Company</th>
              <th className="text-left px-5 py-3">Plan</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Submitted</th>
              <th className="text-left px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">
                No requests in <span className="font-mono text-slate-600">{statusFilter}</span>.
              </td></tr>
            )}
            {items.map(r => (
              <tr key={r.request_id} data-testid={`sa-row-${r.request_id}`}>
                <td className="px-5 py-3">
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-5 py-3 text-slate-700">{r.company || '—'}</td>
                <td className="px-5 py-3 text-slate-700 font-mono text-xs">{r.requested_plan_id || 'monthly'}</td>
                <td className="px-5 py-3">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{formatTs(r.created_at)}</td>
                <td className="px-5 py-3">
                  {r.status === 'pending' ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setApproveTarget(r)}
                        data-testid={`sa-approve-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] uppercase tracking-[0.18em] font-bold bg-[#8B3FC5] text-white hover:bg-[#AE52EA]"
                      >
                        <Check size={12} /> Approve
                      </button>
                      <button
                        onClick={() => setRejectTarget(r)}
                        data-testid={`sa-reject-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700 border border-rose-300 hover:bg-rose-50"
                      >
                        <X size={12} /> Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">
                      {r.reviewed_by_email ? `by ${r.reviewed_by_email}` : '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
        <ShieldCheck size={12} className="text-[#8B3FC5]"/>
        All approvals fire a welcome email through Hostinger SMTP automatically.
        Stripe approvals provision the user only after payment lands via webhook.
      </div>

      {approveTarget && (
        <ApproveModal
          target={approveTarget}
          onClose={() => setApproveTarget(null)}
          onDone={() => { setApproveTarget(null); load(); }}
        />
      )}
      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={() => { setRejectTarget(null); load(); }}
        />
      )}
    </div>
  );
}


function StatusPill({ status }) {
  const map = {
    pending:                  { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Clock, label: 'Pending' },
    approved:                 { color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: Check, label: 'Approved' },
    approved_pending_payment: { color: 'text-violet-700 bg-violet-50 border-violet-200', icon: CreditCard, label: 'Awaiting payment' },
    rejected:                 { color: 'text-rose-700 bg-rose-50 border-rose-200', icon: X, label: 'Rejected' },
  };
  const m = map[status] || { color: 'text-slate-700 bg-slate-50 border-slate-200', icon: FileText, label: status };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] uppercase tracking-[0.16em] font-bold ${m.color}`}>
      <Icon size={10} />
      {m.label}
    </span>
  );
}


function ApproveModal({ target, onClose, onDone }) {
  const [mode, setMode] = useState('trial');
  const [trialDays, setTrialDays] = useState(14);
  const [planId, setPlanId] = useState('monthly');
  const [role, setRole] = useState('founder');
  const [bankInstr, setBankInstr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { mode, role };
      if (mode === 'trial')  body.trial_days = parseInt(trialDays, 10) || 14;
      if (mode === 'stripe') body.plan_id = planId;
      if (mode === 'manual' && bankInstr.trim()) {
        body.bank_instructions_html = `<pre style="white-space:pre-wrap;font-family:inherit;color:#9CA3AF;font-size:12px;line-height:1.7;">${escapeHtml(bankInstr)}</pre>`;
      }
      await api.post(`/subscriptions/requests/${target.request_id}/approve`, body);
      toast.success('Approved.', {
        description: mode === 'stripe'
          ? 'Stripe checkout link emailed; user provisioned on payment.'
          : mode === 'trial'
          ? `User provisioned with ${body.trial_days}-day trial. Welcome email sent.`
          : 'User provisioned. Bank-transfer instructions emailed.',
      });
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approval failed.');
    } finally { setBusy(false); }
  };

  return (
    <Backdrop onClose={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-6 space-y-5" data-testid="sa-approve-modal">
        <div>
          <div className="ve-eyebrow text-[#8B3FC5]">Approve request</div>
          <h3 className="text-lg font-semibold mt-1">{target.name}</h3>
          <div className="text-xs text-slate-500">{target.email}</div>
        </div>

        <div className="grid grid-cols-3 gap-1 p-1 rounded-md border border-slate-200 bg-slate-50">
          <ModeBtn icon={CreditCard} label="Stripe"  selected={mode==='stripe'} onClick={() => setMode('stripe')} testid="sa-mode-stripe" />
          <ModeBtn icon={Gift}       label="Trial"   selected={mode==='trial'}  onClick={() => setMode('trial')}  testid="sa-mode-trial" />
          <ModeBtn icon={FileText}   label="Manual"  selected={mode==='manual'} onClick={() => setMode('manual')} testid="sa-mode-manual" />
        </div>

        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Role on provisioning
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="sa-approve-role"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal focus:border-[#8B3FC5] focus:outline-none"
          >
            <option value="founder">Founder</option>
            <option value="investor">Investor</option>
            <option value="startup_member">Startup member</option>
            <option value="holding_admin">Holding Admin (tenant-scoped)</option>
          </select>
        </label>

        {mode === 'trial' && (
          <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Trial length (days)
            <input
              type="number" min={1} max={365}
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              data-testid="sa-approve-trial-days"
              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal font-mono focus:border-[#8B3FC5] focus:outline-none"
            />
          </label>
        )}

        {mode === 'stripe' && (
          <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Plan
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              data-testid="sa-approve-plan"
              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal focus:border-[#8B3FC5] focus:outline-none"
            >
              <option value="monthly">Monthly · QAR 10,000</option>
              <option value="core_os">Core OS</option>
            </select>
          </label>
        )}

        {mode === 'manual' && (
          <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Bank-transfer instructions (will be emailed verbatim)
            <textarea
              value={bankInstr}
              onChange={(e) => setBankInstr(e.target.value)}
              data-testid="sa-approve-bank-instr"
              rows={5}
              placeholder={"Beneficiary: Vigilant Entities LLC\nIBAN: QA12 ...\nReference: " + target.request_id}
              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal font-mono focus:border-[#8B3FC5] focus:outline-none"
            />
          </label>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            data-testid="sa-approve-cancel"
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="sa-approve-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold hover:bg-[#AE52EA] disabled:opacity-50"
          >
            <Mail size={14} />
            {busy ? 'Approving…' : 'Approve & email user'}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}


function RejectModal({ target, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/subscriptions/requests/${target.request_id}/reject`, { reason: reason.trim() });
      toast.success('Rejected. The requester has been notified.');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rejection failed.');
    } finally { setBusy(false); }
  };
  return (
    <Backdrop onClose={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-6 space-y-4" data-testid="sa-reject-modal">
        <div>
          <div className="ve-eyebrow text-rose-700">Reject request</div>
          <h3 className="text-lg font-semibold mt-1">{target.name}</h3>
          <div className="text-xs text-slate-500">{target.email}</div>
        </div>
        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Reason (will be emailed verbatim)
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="sa-reject-reason"
            rows={4}
            placeholder="e.g. We're not currently onboarding individual founders — try again Q3."
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal focus:border-rose-500 focus:outline-none"
          />
        </label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} data-testid="sa-reject-cancel"
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="sa-reject-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 disabled:opacity-50">
            {busy ? 'Rejecting…' : 'Reject & notify'}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}


function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function ModeBtn({ icon: Icon, label, selected, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2 py-2 rounded text-[10px] uppercase tracking-[0.18em] font-bold flex items-center justify-center gap-1.5 transition-colors ${
        selected
          ? 'bg-[#8B3FC5] text-white shadow-sm'
          : 'text-slate-500 hover:text-slate-900'
      }`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}
